import {UsuarioInfoNavLink, UsuarioInformacao} from '@components/contas/UsuarioInformacao.tsx';
import {
	SolicitacaoTipoInformacaoTextField,
} from '@components/solicitacoes/solicitacao/tipo/SolicitacaoTipoInformacao.tsx';
import type {ISolicitacao} from "@models/solicitacoes/solicitacoes.ts";
import {
	AddComment,
	AutoAwesome,
	Feedback,
	OpenInNew,
	SaveOutlined,
	TransferWithinAStation,
	Verified
} from "@mui/icons-material";
import {Button, Grid, IconButton, TableCell, TableRow, TextField, Typography} from "@mui/material";
import {ToDateDefault, ToDateTimeDefault, ToMoney} from "@shared/MathHelp.ts";
import {MoneyInputEntry} from '@shared/specialComponents/InputEntry.tsx';
import {createSearchParams, useNavigate} from "react-router";
import {DefaultEventProps, DefaultInformationProps, DefaultNavLinkProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import React, {useEffect, useMemo, useState} from "react";
import {NavigationLink} from "@shared/custom/NavigationLink.tsx";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {_SolicitacoesEndpoints} from "@localization/siteMap/endpoints/_SolicitacoesEnpoints.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {CreateQuickFixComponent} from "@components/solicitacoes/solicitacao/CreateQuickFixComponent.tsx";
import {QuickFixesPage} from "@components/solicitacoes/pages/quickfix/QuickFixesPage.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {useSolicitacao} from "@components/solicitacoes/hooks/useSolicitacao.tsx";
import {TransferirSolicitacaoComponent} from "@components/solicitacoes/TransferirSolicitacaoComponent.tsx";

interface SolicitacaoInfoProps extends DefaultInformationProps<ISolicitacao> {
	variant: 'grid' | 'list' | 'navigationLink' | 'row';
}

export const SolicitacaoInfo: React.FC<SolicitacaoInfoProps> = (props) => {
	if (props.variant == 'list') return <SolicitacaoInfoList {...props}/>;
	if (props.variant == 'navigationLink') return <SolicitacaoInfoNavLink {...props}/>;
	if (props.variant == 'row') return <SolicitacaoInfoRow {...props}/>;

	return <SolicitacaoInformacaoGrid {...props}/>;
};

export const SolicitacaoInfoNavLink: React.FC<Omit<SolicitacaoInfoProps, 'variant' | 'EndComponent' | 'overrides'> & DefaultNavLinkProps<ISolicitacao>> = (props) => {
	return (<NavigationLink label={props.value.titulo}
	                        chipSx={{...props.slotProps?.chip?.sx}}
	                        tableMode={props.slotProps?.chip?.tableMode}
	                        pathName={'/solicitacao/detalhes'}
	                        onClick={props.onClick}
	                        toolTipHint={"Ir para a pagina de detalhes da solicitacao " + props.value.titulo}
	                        searchData={{
		                        solicitacaoId: props.value.id.toString(),
	                        }}/>);
};

export const SolicitacaoInfoRow: React.FC<Omit<SolicitacaoInfoProps, 'variant'>> = (props) => {
	const {value, EndComponent} = props;

	return (
		<TableRow>
			<TableCell><Typography
				color={(props.value.dataFinalizacao != null && props.value.itensPendentes == 0) ? 'info' : 'warning'}>{value.id}</Typography></TableCell>
			<TableCell>
				<SolicitacaoInfoNavLink slotProps={{
					chip: {
						tableMode: true,
					},
				}} {...props}/>
			</TableCell>

			<TableCell> {value.solicitante && <UsuarioInfoNavLink slotProps={{
				chip: {
					tableMode: true,
				}
			}} value={value.solicitante}/>}
			</TableCell>
			<TableCell align={'right'}>{ToDateDefault(value.dataCriacao)}</TableCell>
			<TableCell align={'right'}>
				{ToMoney(value.valorEfetivado)} / {ToMoney(value.valorPendente)}
			</TableCell>
			<TableCell>
				{value.itensFinalizados} / {value.itensPendentes}
			</TableCell>
			<TableCell align={'right'}>
				{EndComponent && <EndComponent {...value}/>}
			</TableCell>
		</TableRow>
	);
};

export const SolicitacaoInformacaoGrid: React.FC<Omit<SolicitacaoInfoProps, 'variant'> & Pick<DefaultEventProps, 'onUpdate'>> = (props) => {
	const {adjustSizeLevel, pickCompare} = useGeneral();
	const {finalizarSolicitacao} = useSolicitacao()
	const {userData, hasAccess, permissoes} = useAuth()
	const {addPopUp, createAlertFromResponse, closePopUp} = useAlert()
	const {Get} = useApi()

	const {
		value,
	} = props;

	const [newValue, setNewValue] = useState<ISolicitacao>(value);

	useEffect(() => {
		setNewValue(value);
	}, [value]);

	const podeTransferir = useMemo(() => {
		return hasAccess(['EditarSolicitacaoOutros'])
	}, [permissoes])


	const navigate = useNavigate();

	return (value && <DefaultContainer>
		<Grid container justifyContent={'space-between'}>
			<Grid rowSpacing={1} container>
				<Grid size={12}>
					<Grid container justifyContent={'space-between'}>
						<Grid container spacing={1}>
							<Grid>
								<IconButton disabled={!value.analise}
								            onClick={() => {
									            addPopUp({
										            size: 'lg',
										            content: <Typography sx={{whiteSpace: 'pre-line'}}>
											            {value.analise?.analise}</Typography>,
										            title: 'Analise gerada por IA as ' + ToDateTimeDefault(value.analise?.dataAnalise)
									            })
								            }}>
									<AutoAwesome/>
								</IconButton>
							</Grid>
							<Grid>
								<IconButton
									disabled={(props.value.dataFinalizacao != null && props.value.itensPendentes == 0) || (value.solicitanteId != userData?.id && !hasAccess('EditarSolicitacaoOutros'))}
									color={'success'}
									onClick={() => {
										addPopUp({
											size: 'lg',
											title: 'Deseja finalizar a solicitação?',
											content: <Grid container>
												<Grid size={12}>
													<Typography>Essa ação não pode ser desfeita.</Typography>
													<Typography color={'warning'}>Ao confirmar, todas os itens e
														orçamentos que não tiveram pagamento efetuado serão
														cancelados...</Typography>
													<Typography>Você tem certeza que deseja finalizar a
														solicitação?</Typography>
												</Grid>
												<Grid size={12} mt={1}>
													<Grid container justifyContent={'end'}>
														<Button color={'info'} onClick={() => {
															finalizarSolicitacao({idSolicitacao: value.id}).then(x => {
																if (x.status == 'Success') {
																	setNewValue(x.items);
																	if (props.onUpdate)
																		props.onUpdate(x.items);
																}
																createAlertFromResponse(x)
															}).finally(() => {
																closePopUp()
															});
														}}>
															Confirmar e finalizar
														</Button>
													</Grid>
												</Grid>


											</Grid>
										})

									}}
								>
									<Verified/>
								</IconButton>
							</Grid>
							<Grid>
								<IconButton disabled={!hasAccess(['EditarSolicitacaoOutros'])} onClick={() => {
									addPopUp({
										size: 'lg',
										content: <CreateQuickFixComponent solicitacaoId={value.id}
										                                  onSuccess={() => {
											                                  if (props.onUpdate) {
												                                  props.onUpdate({});
												                                  closePopUp()
											                                  }
										                                  }}/>,
										title: 'Criar requisição de correção rápida para a solicitação',
									})
								}}>
									<AddComment/>
								</IconButton>
							</Grid>
							<Grid>
								<IconButton disabled={value.quickFixes?.length == 0}
								            onClick={() => {
									            addPopUp({
										            content: <QuickFixesPage onAction={() => {
											            if (props.onUpdate) {
												            props.onUpdate({});
											            }
										            }} solicitacaoId={value.id}/>,
										            title: 'Quick Fixes da solicitação ' + value.id,
										            size: 'lg',
									            })
								            }}
								            color={((value.quickFixes?.filter(x => !x.dataAutorizacao)?.length ?? 0) > 0 ? 'warning' : 'inherit')}>
									<Feedback/>
								</IconButton>
							</Grid>
							<Grid>
								<IconButton disabled={!podeTransferir} onClick={() => {
									addPopUp({
										title: 'Transferindo solicitação',
										content: <TransferirSolicitacaoComponent solicitacao={value} onUpdate={(x) => {
											createAlertFromResponse(x)
											closePopUp()
										}}/>,
									})
								}}>
									<TransferWithinAStation/>
								</IconButton>
							</Grid>
						</Grid>
						<Grid>
							<Button variant={'outlined'}
							        disabled={pickCompare(value, newValue, ['contaContabilId', 'contratoId', 'titulo'])}
							        onClick={() => {
								        Get(_SolicitacoesEndpoints.UpdateSolicitacao(newValue)).then(x => {
									        if (x.status == 'Success') {
										        setNewValue(x.items);
										        if (props.onUpdate)
											        props.onUpdate(x.items);
									        }
									        createAlertFromResponse(x)
								        })
							        }}
							        endIcon={<SaveOutlined/>}>Salvar</Button>
						</Grid>
					</Grid>
				</Grid>

				<Grid size={adjustSizeLevel({micro: 12, md: 3})}><TextField label={'Id'} fullWidth disabled
				                                                            value={value.id}/></Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 9})}><TextField label={'Titulo'} fullWidth
				                                                            disabled={!hasAccess(['EditarSolicitacaoOutros']) && userData?.id != value.solicitante?.id}
				                                                            onChange={e => {
					                                                            setNewValue({
						                                                            ...newValue,
						                                                            titulo: e.target.value,
					                                                            });
				                                                            }}
				                                                            value={newValue.titulo}/></Grid>
				{value.tipoSolicitacao && <Grid size={12}>
					<SolicitacaoTipoInformacaoTextField value={value.tipoSolicitacao} displayMode={'nome'}
					                                    tooltip={'descricao'}/>
				</Grid>}
				<Grid size={adjustSizeLevel({micro: 12, md: 6})}>
					<TextField fullWidth label={'Data de abertura'} disabled
					           value={ToDateDefault(value.dataCriacao)}/>
				</Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 6})}>
					<TextField fullWidth label={'Aberto por'} disabled value={value.solicitante?.nome}/>
				</Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 6})}>
					<TextField disabled
					           fullWidth
					           label={'Recurso' + (!value.recurso?.isAtivo ? ' (Inativo)' : '')}
					           value={value.recurso?.nome}
					           error={!value.recurso?.isAtivo}
					           slotProps={{
						           input: {
							           endAdornment: <IconButton size={'small'} onClick={() => {
								           if (value.recursoId != null)
									           navigate({
										           pathname: '/recurso/detalhes',
										           search: createSearchParams({
											           recursoId: value.recursoId.toString(),
										           }).toString(),
									           });
							           }}>
								           <OpenInNew/>
							           </IconButton>,
						           },
					           }}
					/>
				</Grid>
				{<Grid size={adjustSizeLevel({micro: 12, md: 6})}>
					<TextField disabled
					           fullWidth
					           label={'Conta Contabil' + (!value.contaContabil?.isAtivo ? ' (Inativo)' : '')}
					           value={value.contaContabil?.nome}
					           error={!value.contaContabil?.isAtivo}
					           slotProps={{
						           input: {
							           endAdornment: <IconButton disabled size={'small'} onClick={() => {
								           if (value.recursoId != null)
									           navigate({
										           pathname: '/contacontabil/detalhes',
										           search: createSearchParams({
											           recursoId: value.recursoId.toString(),
										           }).toString(),
									           });
							           }}>
								           <OpenInNew/>
							           </IconButton>,
						           },
					           }}/>
				</Grid>}
				<Grid size={adjustSizeLevel({micro: 12, md: 6, lg: 3})}>
					<TextField disabled
					           fullWidth
					           label={'Itens pendentes'}
					           value={value.itensPendentes}/>
				</Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 6, lg: 3})}>
					<TextField disabled
					           fullWidth
					           label={'Itens finalizados'}
					           value={value.itensFinalizados}/>
				</Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 6, lg: 3})}>
					<MoneyInputEntry label={'Custo total da solicitação'}
					                 value={value.valorEfetivado} onChange={() => {
					}} disabled/>
				</Grid>
				<Grid size={adjustSizeLevel({micro: 12, md: 6, lg: 3})}>
					<MoneyInputEntry disabled onChange={() => {
					}} value={value.valorPendente} label={'Custo pendente'}/>
				</Grid>
			</Grid>
		</Grid>
	</DefaultContainer>);
};

const SolicitacaoInfoList: React.FC<Omit<SolicitacaoInfoProps, 'variant'>> = (props) => {
	const {screenSizeLevel} = useGeneral();

	const {
		value,
		EndComponent,
	} = props;


	return (
		<Grid container justifyContent={'space-between'}>
			<Grid size={'grow'}>
				<Grid container spacing={1} alignContent={'center'}>
					<Grid>
						<Typography
							color={(props.value.dataFinalizacao != null && props.value.itensPendentes == 0) ? 'info' : 'warning'}>
							{value.id}
						</Typography>
					</Grid>
					<Grid><SolicitacaoInfoNavLink {...props}/></Grid>
					{screenSizeLevel <= 6 && value.solicitante &&
						<UsuarioInformacao value={value.solicitante} variant={'navigationLink'}/>}
					<Grid>
						<Typography>Em {ToDateDefault(value.dataCriacao)}</Typography>
					</Grid>
					{(value.valorEfetivado && value.valorEfetivado > 0) &&
						<Grid>
							<Typography>Custo efetivo: {ToMoney(value.valorEfetivado)}</Typography>
						</Grid>
					}
				</Grid>
			</Grid>
			{EndComponent && <EndComponent {...value}/>}
		</Grid>
	);

};